import { useState, useRef, useCallback, useEffect } from 'react';
import { Camera, X, ChevronLeft, ChevronRight, Edit2, Check, GripVertical } from 'lucide-react';
import { toast } from 'sonner';
import * as Dialog from '@radix-ui/react-dialog';
import { compressImage, formatCompressionResult } from '@/lib/photo/imageCompression';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import { api } from '@/lib/api';
import { useQueryClient } from '@tanstack/react-query';
import {
  useUploadVisitPhoto,
  useUpdateVisitPhoto,
  useDeleteVisitPhoto,
  useVisitPhotoUrl,
  type VisitPhoto,
} from '@/hooks/useSiteVisits';

interface Props {
  visitId: string;
  photos: VisitPhoto[];
  isEditable: boolean;
}

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB — matches backend cap

// Map MIME type to file extension. Default to 'jpg' so we always emit something usable.
const MIME_EXT: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/gif': 'gif',
  'image/heic': 'heic',
  'image/heif': 'heif',
};

// Drag-drop / paste produces a File whose name is 'blob' (or empty) because the
// underlying object is a Blob, not a File. Derive a real filename so exported
// site-visit reports don't show every photo as "blob". Issue #81.
export function deriveUploadFilename(file: File): string {
  const name = file.name?.trim();
  if (name && name !== 'blob') return name;
  const ext = MIME_EXT[file.type] || 'jpg';
  return `site-visit-photo-${Date.now()}.${ext}`;
}

function withDerivedName(file: File): File {
  const derived = deriveUploadFilename(file);
  if (derived === file.name) return file;
  return new File([file], derived, { type: file.type, lastModified: file.lastModified });
}

// Fetches photo with auth token, displays via blob URL
function AuthenticatedPhoto({ visitId, photoId, alt, className }: {
  visitId: string;
  photoId: string;
  alt: string;
  className?: string;
}) {
  const { data: blobUrl, isLoading, isError } = useVisitPhotoUrl(visitId, photoId);

  if (isLoading) {
    return <div className={cn('bg-muted animate-pulse', className)} />;
  }
  if (isError || !blobUrl) {
    return (
      <div className={cn('bg-muted flex items-center justify-center text-muted-foreground text-xs', className)}>
        <Camera className="w-4 h-4 opacity-40" />
      </div>
    );
  }
  return <img src={blobUrl} alt={alt} className={className} loading="lazy" />;
}

// Sortable photo card (used when editable)
interface SortablePhotoProps {
  photo: VisitPhoto;
  visitId: string;
  idx: number;
  isEditable: boolean;
  editingPhotoId: string | null;
  editCaption: string;
  editTagInput: string;
  editTags: string[];
  onOpenLightbox: (idx: number, e: React.MouseEvent<HTMLElement>) => void;
  onOpenEdit: (photo: VisitPhoto) => void;
  onCancelEdit: () => void;
  onSaveEdit: () => void;
  onSetEditCaption: (v: string) => void;
  onSetEditTagInput: (v: string) => void;
  onAddTag: () => void;
  onRemoveTag: (tag: string) => void;
  onDeleteConfirm: (id: string) => void;
}

function SortablePhotoCard({
  photo, visitId, idx, isEditable,
  editingPhotoId, editCaption, editTagInput, editTags,
  onOpenLightbox, onOpenEdit, onCancelEdit, onSaveEdit,
  onSetEditCaption, onSetEditTagInput, onAddTag, onRemoveTag, onDeleteConfirm,
}: SortablePhotoProps) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: photo.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : undefined,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <div className="relative group">
        {/* Drag handle (editable only) */}
        {isEditable && (
          <button
            className="absolute top-1 right-7 bg-black/50 text-white rounded p-0.5 opacity-60 hover:opacity-100 focus-visible:opacity-100 transition-opacity cursor-grab active:cursor-grabbing z-10 min-h-[28px] min-w-[28px] flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
            title="Drag to reorder"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="w-3 h-3" />
          </button>
        )}

        <button
          className="block w-full aspect-square bg-muted rounded overflow-hidden"
          onClick={(e) => onOpenLightbox(idx, e)}
        >
          <AuthenticatedPhoto
            visitId={visitId}
            photoId={photo.id}
            alt={photo.caption || photo.file_name}
            className="w-full h-full object-cover"
          />
        </button>

        {isEditable && (
          <>
            <button
              className="absolute top-1 right-1 bg-black/50 text-white rounded p-0.5 opacity-60 hover:opacity-100 focus-visible:opacity-100 transition-opacity min-h-[28px] min-w-[28px] flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
              onClick={(e) => { e.stopPropagation(); onDeleteConfirm(photo.id); }}
              title="Delete photo"
            >
              <X className="w-3 h-3" />
            </button>
            <button
              className="absolute top-1 left-1 bg-black/50 text-white rounded p-0.5 opacity-60 hover:opacity-100 focus-visible:opacity-100 transition-opacity min-h-[28px] min-w-[28px] flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
              onClick={(e) => {
                e.stopPropagation();
                editingPhotoId === photo.id ? onCancelEdit() : onOpenEdit(photo);
              }}
              title="Edit caption & tags"
            >
              <Edit2 className="w-3 h-3" />
            </button>
          </>
        )}
      </div>

      {/* Caption (read mode) */}
      {photo.caption && editingPhotoId !== photo.id && (
        <p
          className="text-xs text-muted-foreground line-clamp-3 mt-0.5 px-0.5"
          title={photo.caption}
        >
          {photo.caption}
        </p>
      )}

      {/* Tag pills (read mode) */}
      {photo.tags && photo.tags.length > 0 && editingPhotoId !== photo.id && (
        <div className="flex flex-wrap gap-0.5 mt-0.5">
          {photo.tags.map((tag) => (
            <span key={tag} className="text-[9px] bg-muted px-1 rounded text-muted-foreground">
              {tag}
            </span>
          ))}
        </div>
      )}

      {/* Inline edit panel */}
      {editingPhotoId === photo.id && (
        <div className="mt-1 space-y-1">
          <Textarea
            value={editCaption}
            onChange={(e) => onSetEditCaption(e.target.value)}
            placeholder="Caption..."
            className="text-xs min-h-[unset] resize-none py-1 px-2"
            style={{ maxHeight: '96px' }}
            rows={2}
            maxLength={500}
            autoFocus
            onInput={(e) => {
              const el = e.currentTarget;
              el.style.height = 'auto';
              el.style.height = Math.min(el.scrollHeight, 96) + 'px';
            }}
          />
          {editTags.length > 0 && (
            <div className="flex flex-wrap gap-0.5">
              {editTags.map((tag) => (
                <span
                  key={tag}
                  className="flex items-center gap-0.5 text-[9px] bg-blue-100 text-blue-700 px-1 rounded"
                >
                  {tag}
                  <button onClick={() => onRemoveTag(tag)}>
                    <X className="w-2 h-2" />
                  </button>
                </span>
              ))}
            </div>
          )}
          <div className="flex gap-1">
            <Input
              value={editTagInput}
              onChange={(e) => onSetEditTagInput(e.target.value)}
              placeholder="Add tag, Enter..."
              className="h-6 text-xs flex-1"
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); onAddTag(); }
              }}
            />
            <button
              className="p-1 bg-green-100 text-green-700 rounded hover:bg-green-200 min-h-[28px] min-w-[28px] flex items-center justify-center"
              onClick={onSaveEdit}
              title="Save"
            >
              <Check className="w-3 h-3" />
            </button>
            <button
              className="p-1 bg-muted rounded hover:bg-muted/80 min-h-[28px] min-w-[28px] flex items-center justify-center"
              onClick={onCancelEdit}
              title="Cancel"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function SiteVisitPhotoGallery({ visitId, photos, isEditable }: Props) {
  const uploadPhoto = useUploadVisitPhoto();
  const updatePhoto = useUpdateVisitPhoto();
  const deletePhoto = useDeleteVisitPhoto();
  const queryClient = useQueryClient();

  const fileInputRef = useRef<HTMLInputElement>(null);
  // Tracks the button that opened the lightbox so focus can be restored on close
  // (issue #74). Radix Dialog can't auto-restore here because there's no static
  // <Dialog.Trigger> — the lightbox is driven by index state, not one trigger.
  const lightboxTriggerRef = useRef<HTMLElement | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [compressionStatus, setCompressionStatus] = useState<string | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [editingPhotoId, setEditingPhotoId] = useState<string | null>(null);
  const [editCaption, setEditCaption] = useState('');
  const [editTagInput, setEditTagInput] = useState('');
  const [editTags, setEditTags] = useState<string[]>([]);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  // Local ordered photo list for optimistic reorder
  const [localPhotos, setLocalPhotos] = useState<VisitPhoto[]>(photos);

  // Sync when props change (new upload, etc.)
  useEffect(() => {
    setLocalPhotos(photos);
  }, [photos]);

  // dnd-kit sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleFiles = useCallback(
    async (files: FileList | File[]) => {
      for (const file of Array.from(files)) {
        if (file.size > MAX_FILE_SIZE) {
          toast.error(`Photo too large (max 10MB)`);
          continue;
        }
        setCompressionStatus('Compressing...');
        const result = await compressImage(file, (pct) => {
          setCompressionStatus(`Compressing... ${pct}%`);
        });
        setCompressionStatus(null);
        await uploadPhoto.mutateAsync({ visitId, file: withDerivedName(result.file) });
        if (result.wasCompressed) {
          toast.success(`${file.name}: ${formatCompressionResult(result.originalSize, result.compressedSize)}`);
        }
      }
    },
    [visitId, uploadPhoto],
  );

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      await handleFiles(e.target.files);
      e.target.value = '';
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    await handleFiles(e.dataTransfer.files);
  };

  const openEdit = (photo: VisitPhoto) => {
    setEditingPhotoId(photo.id);
    setEditCaption(photo.caption || '');
    setEditTags(photo.tags || []);
    setEditTagInput('');
  };

  const saveEdit = async () => {
    if (!editingPhotoId) return;
    await updatePhoto.mutateAsync({
      visitId,
      photoId: editingPhotoId,
      data: { caption: editCaption, tags: editTags },
    });
    setEditingPhotoId(null);
  };

  const addTag = () => {
    const tag = editTagInput.trim();
    if (tag && !editTags.includes(tag)) {
      setEditTags([...editTags, tag]);
    }
    setEditTagInput('');
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = localPhotos.findIndex((p) => p.id === active.id);
    const newIndex = localPhotos.findIndex((p) => p.id === over.id);
    const reordered = arrayMove(localPhotos, oldIndex, newIndex);

    // Optimistic update
    setLocalPhotos(reordered);

    // Persist sort_order for each photo that moved
    try {
      await Promise.all(
        reordered.map((photo, idx) =>
          api.siteVisits.photos.update(visitId, photo.id, { sort_order: idx }),
        ),
      );
      queryClient.invalidateQueries({ queryKey: ['site-visit', visitId] });
    } catch {
      toast.error('Failed to save photo order');
      queryClient.invalidateQueries({ queryKey: ['site-visit', visitId] });
    }
  };

  const handleDeleteConfirmed = () => {
    if (!deleteConfirmId) return;
    deletePhoto.mutate({ visitId, photoId: deleteConfirmId });
    setDeleteConfirmId(null);
  };

  // Open lightbox AND remember which button triggered it so focus can be
  // restored on close (issue #74).
  const openLightboxFromEvent = useCallback(
    (idx: number, e?: React.MouseEvent<HTMLElement>) => {
      lightboxTriggerRef.current = (e?.currentTarget as HTMLElement) ?? null;
      setLightboxIndex(idx);
    },
    [],
  );

  const prevPhoto = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (lightboxIndex === null) return;
    setLightboxIndex((lightboxIndex - 1 + localPhotos.length) % localPhotos.length);
  };

  const nextPhoto = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (lightboxIndex === null) return;
    setLightboxIndex((lightboxIndex + 1) % localPhotos.length);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          Photos ({localPhotos.length})
        </h4>
        {isEditable && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              multiple
              className="hidden"
              onChange={handleFileChange}
            />
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-xs"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadPhoto.isPending}
            >
              <Camera className="w-3.5 h-3.5 mr-1" />
              {compressionStatus || (uploadPhoto.isPending ? 'Uploading...' : 'Add Photos')}
            </Button>
          </>
        )}
      </div>

      {/* Drop zone */}
      {isEditable && (
        <div
          className={cn(
            'border-2 border-dashed rounded-lg p-4 mb-3 text-center text-sm text-muted-foreground transition-colors cursor-pointer',
            isDragOver
              ? 'border-blue-400 bg-blue-50 text-blue-600'
              : 'border-muted hover:border-muted-foreground/40',
            (uploadPhoto.isPending || !!compressionStatus) && 'opacity-60 pointer-events-none',
          )}
          onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
          onDragLeave={() => setIsDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <Camera className="w-5 h-5 mx-auto mb-1 opacity-40" />
          {compressionStatus || (uploadPhoto.isPending ? 'Uploading...' : 'Drop photos here or click to upload')}
        </div>
      )}

      {/* Photo grid */}
      {localPhotos.length === 0 ? (
        <div className="text-xs text-muted-foreground italic py-2">No photos yet</div>
      ) : isEditable ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={localPhotos.map((p) => p.id)} strategy={rectSortingStrategy}>
            <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
              {localPhotos.map((photo, idx) => (
                <SortablePhotoCard
                  key={photo.id}
                  photo={photo}
                  visitId={visitId}
                  idx={idx}
                  isEditable={isEditable}
                  editingPhotoId={editingPhotoId}
                  editCaption={editCaption}
                  editTagInput={editTagInput}
                  editTags={editTags}
                  onOpenLightbox={openLightboxFromEvent}
                  onOpenEdit={openEdit}
                  onCancelEdit={() => setEditingPhotoId(null)}
                  onSaveEdit={saveEdit}
                  onSetEditCaption={setEditCaption}
                  onSetEditTagInput={setEditTagInput}
                  onAddTag={addTag}
                  onRemoveTag={(tag) => setEditTags(editTags.filter((t) => t !== tag))}
                  onDeleteConfirm={setDeleteConfirmId}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        // Read-only grid (no dnd overhead)
        <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
          {localPhotos.map((photo, idx) => (
            <div key={photo.id}>
              <button
                className="block w-full aspect-square bg-muted rounded overflow-hidden"
                onClick={(e) => openLightboxFromEvent(idx, e)}
              >
                <AuthenticatedPhoto
                  visitId={visitId}
                  photoId={photo.id}
                  alt={photo.caption || photo.file_name}
                  className="w-full h-full object-cover"
                />
              </button>
              {photo.caption && (
                <p
                  className="text-xs text-muted-foreground line-clamp-3 mt-0.5 px-0.5"
                  title={photo.caption}
                >
                  {photo.caption}
                </p>
              )}
              {photo.tags && photo.tags.length > 0 && (
                <div className="flex flex-wrap gap-0.5 mt-0.5">
                  {photo.tags.map((tag) => (
                    <span key={tag} className="text-[9px] bg-muted px-1 rounded text-muted-foreground">
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Delete confirm dialog */}
      <AlertDialog open={!!deleteConfirmId} onOpenChange={() => setDeleteConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Photo</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the photo and remove it from Firebase Storage.
              Any checklist items linking this photo will lose their evidence reference.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirmed}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Lightbox — Radix Dialog for real focus trap, Escape, and ARIA.
          Issue #74: restore focus to the originating photo button on close
          (via onCloseAutoFocus + lightboxTriggerRef).
          Issue #82: Content uses `pointer-events-none` so clicks on the dark
          area pass through to Dialog.Overlay, which Radix treats as
          pointer-down-outside and closes the dialog. Interactive children opt
          back in with `pointer-events-auto`. */}
      <Dialog.Root
        open={lightboxIndex !== null}
        onOpenChange={(open) => { if (!open) setLightboxIndex(null); }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/80 z-50" />
          <Dialog.Content
            className="fixed inset-0 z-50 flex items-center justify-center p-4 outline-none pointer-events-none"
            aria-describedby={undefined}
            onCloseAutoFocus={(e) => {
              const trigger = lightboxTriggerRef.current;
              if (trigger && document.contains(trigger)) {
                e.preventDefault();
                trigger.focus();
              }
              lightboxTriggerRef.current = null;
            }}
          >
            <Dialog.Title className="sr-only">Photo viewer</Dialog.Title>

            {lightboxIndex !== null && localPhotos[lightboxIndex] && (
              <>
                {/* Prev */}
                {localPhotos.length > 1 && (
                  <button
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-white bg-black/40 rounded-full p-2 hover:bg-black/60 min-h-[44px] min-w-[44px] flex items-center justify-center pointer-events-auto"
                    onClick={prevPhoto}
                    aria-label="Previous photo"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                )}

                {/* Image */}
                <div className="flex flex-col items-center gap-2 pointer-events-auto">
                  <AuthenticatedPhoto
                    visitId={visitId}
                    photoId={localPhotos[lightboxIndex].id}
                    alt={localPhotos[lightboxIndex].caption || localPhotos[lightboxIndex].file_name}
                    className="max-w-full max-h-[80vh] object-contain rounded"
                  />
                  {localPhotos[lightboxIndex].caption && (
                    <p className="text-white text-sm text-center">{localPhotos[lightboxIndex].caption}</p>
                  )}
                  <p className="text-white/50 text-xs">
                    {lightboxIndex + 1} / {localPhotos.length}
                  </p>
                </div>

                {/* Next */}
                {localPhotos.length > 1 && (
                  <button
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-white bg-black/40 rounded-full p-2 hover:bg-black/60 min-h-[44px] min-w-[44px] flex items-center justify-center pointer-events-auto"
                    onClick={nextPhoto}
                    aria-label="Next photo"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                )}

                {/* Close */}
                <Dialog.Close
                  className="absolute top-4 right-4 text-white bg-black/40 rounded-full p-1 hover:bg-black/60 min-h-[44px] min-w-[44px] flex items-center justify-center pointer-events-auto"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </Dialog.Close>
              </>
            )}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}
